/**
 * Client flags for the post-registration subscription gate and left-menu notices.
 * Existing pumps without subscription_required are not blocked, but may still see a “choose a plan” banner.
 */
export const SUBSCRIPTION_BLOCKED_KEY = 'SUBSCRIPTION_BLOCKED'
export const SUBSCRIPTION_REQUIRED_KEY = 'SUBSCRIPTION_REQUIRED'
export const SUBSCRIPTION_EXPIRED_KEY = 'SUBSCRIPTION_EXPIRED'
export const SUBSCRIPTION_NEEDS_PLAN_KEY = 'SUBSCRIPTION_NEEDS_PLAN'
export const SUBSCRIPTION_END_DATE_KEY = 'SUBSCRIPTION_END_DATE'

export const SUBSCRIPTION_GATE_SCREENS = [
  'pump-details',
  'subscription-management',
  'subscription',
  'payment-Success',
  'payment-Failed',
  'invoice-settings',
  'logout',
]

export function persistSubscriptionAccess(payload = {}) {
  const hasExplicitFlag = Object.prototype.hasOwnProperty.call(payload, 'subscription_blocked')
    || Object.prototype.hasOwnProperty.call(payload, 'subscription_required')
    || Object.prototype.hasOwnProperty.call(payload, 'subscription_expired')
    || Object.prototype.hasOwnProperty.call(payload, 'subscription_needs_plan')
    || Object.prototype.hasOwnProperty.call(payload, 'needs_pump')
    || Boolean(payload.access)

  if (!hasExplicitFlag) {
    return
  }

  const access = payload.access || {}
  const blocked = Boolean(
    payload.subscription_blocked
      || payload.needs_pump
      || access.blocked
  )
  const required = Boolean(
    payload.subscription_required
      || payload.needs_pump
      || access.required
      || access.needs_pump
  )
  const expired = isTruthyFlag(payload.subscription_expired)
    || isTruthyFlag(access.show_renew_notice)
    || isTruthyFlag(access.subscription_expired)
  const needsPlan = isTruthyFlag(payload.subscription_needs_plan)
    || isTruthyFlag(access.show_subscribe_notice)
    || (blocked && !expired)
  const endDate = payload.subscription_end_date
    || access.expired_end_date
    || ''

  if (blocked) {
    localStorage.setItem(SUBSCRIPTION_BLOCKED_KEY, '1')
  } else {
    localStorage.removeItem(SUBSCRIPTION_BLOCKED_KEY)
  }

  if (required) {
    localStorage.setItem(SUBSCRIPTION_REQUIRED_KEY, '1')
  } else {
    localStorage.removeItem(SUBSCRIPTION_REQUIRED_KEY)
  }

  if (expired) {
    localStorage.setItem(SUBSCRIPTION_EXPIRED_KEY, '1')
    if (endDate) {
      localStorage.setItem(SUBSCRIPTION_END_DATE_KEY, String(endDate))
    }
    localStorage.removeItem(SUBSCRIPTION_NEEDS_PLAN_KEY)
  } else {
    localStorage.removeItem(SUBSCRIPTION_EXPIRED_KEY)
    localStorage.removeItem(SUBSCRIPTION_END_DATE_KEY)
    if (needsPlan) {
      localStorage.setItem(SUBSCRIPTION_NEEDS_PLAN_KEY, '1')
    } else {
      localStorage.removeItem(SUBSCRIPTION_NEEDS_PLAN_KEY)
    }
  }
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

export function isSubscriptionAccessBlocked() {
  return localStorage.getItem(SUBSCRIPTION_BLOCKED_KEY) === '1'
}

export function isPaidSubscriptionExpired() {
  return localStorage.getItem(SUBSCRIPTION_EXPIRED_KEY) === '1'
}

/** No active plan — left-menu “choose a subscription” banner (may still have app access). */
export function needsSubscriptionPlan() {
  return localStorage.getItem(SUBSCRIPTION_NEEDS_PLAN_KEY) === '1' && !isPaidSubscriptionExpired()
}

export function storedSubscriptionEndDate() {
  return localStorage.getItem(SUBSCRIPTION_END_DATE_KEY) || ''
}

export function hasStoredPumpId() {
  const pumpId = localStorage.getItem('PUMP_ID')
  return Boolean(pumpId && pumpId !== 'null' && pumpId !== 'undefined' && pumpId !== '0')
}

export function isSubscriptionGatePath(pathname) {
  const segment = String(pathname || '/')
    .replace(/^\//, '')
    .split('/')[0]
  return SUBSCRIPTION_GATE_SCREENS.includes(segment)
}

export function subscriptionResumePath() {
  return hasStoredPumpId() ? '/subscription-management' : '/pump-details'
}

/** Screens that should not win after a successful trial/paid activation. */
const POST_ACTIVATION_IGNORE = new Set([
  'subscription-management',
  'subscription',
  'payment-Success',
  'payment-Failed',
  'logout',
  '',
])

/**
 * Prefer API post-activation screen, then LAST_SCREEN, then dashboard.
 * Ignores subscription gate paths so checkout does not bounce to pump-details.
 */
export function resolvePostActivationPath(apiResumeScreen, fallback = '/dashboard') {
  const candidates = [
    apiResumeScreen,
    typeof localStorage !== 'undefined' ? localStorage.getItem('LAST_SCREEN') : '',
  ]

  for (const raw of candidates) {
    const segment = String(raw || '')
      .trim()
      .replace(/^\//, '')
      .split('/')[0]
    if (segment && !POST_ACTIVATION_IGNORE.has(segment)) {
      return authPathname(segment, fallback)
    }
  }

  return fallback.startsWith('/') ? fallback : `/${fallback}`
}

export function authPathname(lastScreen, fallback = '/pump-details') {
  const screen = String(lastScreen || fallback).trim()
  if (!screen) {
    return fallback
  }
  return screen.startsWith('/') ? screen : `/${screen}`
}
